// Centralizes the business validation rules that used to live scattered
// across public/index.html's save*() handlers (each mixing its own
// validation conditionals directly into DOM-touching code) into pure,
// independently testable functions. Every function here takes plain data
// and returns a result of the shape `{ ok: true, ...extra }` or
// `{ ok: false, message, ...extra }` — no DOM access, no toast()/focus()
// calls, same dependency-injection principle as every other extracted
// module. The still-inline caller reads the result and does the
// UI-specific part (show the message, jump to a step, focus a field).
//
// Extracted from public/index.html's inline script (post-Phase-2 hardening
// pass, "Track 2" — REFACTOR_SECURITY_REPORT.md §19). Every message string
// and every check's order was copied verbatim from the original inline
// conditionals, including exact punctuation (curly apostrophes, em/en
// dashes) — verified byte-for-byte before the originals were removed.
import { normalizePhone } from '../utils/phone.js';
import { getOrderItems } from '../services/pricing.js';
import { exactDuplicateOf } from '../services/orders.js';

// ---------- order form (multi-step: 1 client/address, 2 items, 3 payment) ----------
export function validateOrderForm(o, products, orders, excludeId) {
  if (!o.name || !normalizePhone(o.phone).valid || !o.city || !o.address) {
    return { ok: false, message: 'Complétez le client, le téléphone, la ville et l’adresse', step: 1 };
  }
  const items = getOrderItems(products, o);
  if (!items.some(item => item.product)) {
    return { ok: false, message: 'Ajoutez au moins un produit', step: 2 };
  }
  if (items.some(item => Number(item.unitPrice || 0) <= 0)) {
    return { ok: false, message: 'Un produit a un prix nul. Corrigez-le avant d’enregistrer', step: 2 };
  }
  if (o.paymentStatus === 'received' && !o.paymentAccount) {
    return { ok: false, message: 'Choisissez le compte qui a reçu le paiement', step: 3 };
  }
  const duplicate = exactDuplicateOf(orders, products, o, excludeId);
  if (duplicate) {
    return { ok: false, message: 'Doublon exact bloqué : ' + duplicate.orderNo, step: 1 };
  }
  return { ok: true };
}

// ---------- products / packaging ----------
// Both forms share the identical two-part rule (name required; a new item
// with a nonzero starting stock needs a note explaining where it came
// from) — saveProduct() has both checks inline (the second one easy to
// miss at a glance, buried after the name check and several unrelated
// field reads), savePackaging() has the same two checks back to back.
export function validateProductForm({ name, stock, initialNote, isNew }) {
  if (!String(name || '').trim()) return { ok: false, message: 'Nom requis' };
  if (isNew && stock > 0 && !String(initialNote || '').trim()) {
    return { ok: false, message: 'Ajoutez une note pour le stock initial' };
  }
  return { ok: true };
}

export function validatePackagingForm({ name, stock, initialNote, isNew }) {
  if (!String(name || '').trim()) return { ok: false, message: 'Nom requis' };
  if (isNew && stock > 0 && !String(initialNote || '').trim()) {
    return { ok: false, message: 'Ajoutez une note pour le stock initial' };
  }
  return { ok: true };
}

// ---------- wallet ----------
export function validateWalletTransaction({ amount, date, accountId, note }) {
  if (!(amount > 0) || !date || !accountId) return { ok: false, message: 'Montant, date et compte sont requis' };
  if (!String(note || '').trim()) return { ok: false, message: 'Ajoutez une note pour garder une trace claire', field: 'wtNote' };
  return { ok: true };
}

export function validateOrderPayment({ status, account, date }) {
  if (status === 'received' && (!account || !date)) {
    return { ok: false, message: 'Choisissez le compte et la date de réception' };
  }
  return { ok: true };
}

// current/target/note — the "does this actually change anything" check
// (Math.abs(diff)<.001) is a business rule on computed state, same as it
// was inline, not just a raw-input check; kept here rather than split out
// so callers get one answer instead of two separate calls to sequence.
export function validateAccountAdjustment({ current, target, note }) {
  if (!Number.isFinite(target) || !String(note || '').trim()) {
    return { ok: false, message: 'Nouveau solde et motif requis' };
  }
  const diff = target - current;
  if (Math.abs(diff) < 0.001) return { ok: false, message: 'Le solde ne change pas', icon: 'info' };
  return { ok: true, diff };
}

// ---------- inventory ----------
// Same reasoning as validateAccountAdjustment: `after` is derived from
// `type`/`qty`/`before`, and the original inline checks that derived value
// (negative stock, no-op) alongside the raw-input checks in one sequence.
export function validateStockMovement({ type, qty, note, before }) {
  if (!Number.isFinite(qty) || qty < 0 || (type !== 'set' && qty === 0)) {
    return { ok: false, message: 'Saisissez une quantité valide' };
  }
  if (!String(note || '').trim()) return { ok: false, message: 'Ajoutez une note pour expliquer la modification', field: 'smNote' };
  const after = type === 'entry' ? before + qty : type === 'exit' ? before - qty : qty;
  if (after < 0) return { ok: false, message: 'La sortie dépasse le stock disponible' };
  if (after === before) return { ok: false, message: 'Le stock ne change pas' };
  return { ok: true, after };
}

// ---------- members ----------
export function validateNewMember({ uid, email }) {
  if (!String(uid || '').trim() || !String(email || '').trim()) {
    return { ok: false, message: 'UID et e-mail requis' };
  }
  return { ok: true };
}
